import { paymentMethodsIn2c2p } from '../constants';

import type {
  EwalletType,
  PaymentMethodType,
  VirtualAccountBankAbbr,
} from './enums';

// Country code in uppercase according to ISO 3166-1 alpha 2 https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
export const country = [
  'AU',
  'HK',
  'ID',
  'MY',
  'NZ',
  'PH',
  'SG',
  'TH',
  'VN',
] as const;

export type Country = (typeof country)[number];

export const amexCardSupport: Country[] = ['ID', 'PH', 'HK', 'SG', 'AU', 'NZ'];

// These are the available payment methods for each country
export const paymentMethodsByCountry: Record<Country, PaymentMethodType[]> = {
  ID: [
    'CreditCard',
    'IdOnlineBanking',
    'IdEWallet',
    'IdQr',
    'Invoice',
    'IdVirtualAccount',
  ],
  MY: ['CreditCard', 'Fpx', 'Invoice'],
  PH: ['CreditCard', 'PhOnlineBanking', 'PhEWallet', 'Invoice', 'PhBnpl'],
  HK: ['CreditCard', 'HkFps', 'PayPal', 'HkEWallet', 'Invoice'],
  SG: ['CreditCard', 'PayNowQR', 'Invoice'],
  TH: ['CreditCard', 'ThOnlineBanking', 'PromptPay', 'Invoice'],
  AU: ['CreditCard', 'Invoice'],
  NZ: ['CreditCard', 'Invoice'],
  VN: [],
};

// These are the available payment methods for each country when there are multiple invoices
export const paymentMethodsByCountryForMultipleInvoices: Record<
  Country,
  PaymentMethodType[]
> = {
  ID: ['CreditCard'],
  MY: ['CreditCard', 'Fpx'],
  PH: ['CreditCard'],
  HK: ['CreditCard'],
  SG: ['CreditCard', 'PayNowQR'],
  TH: ['CreditCard', 'PromptPay'],
  AU: ['CreditCard'],
  NZ: ['CreditCard'],
  VN: [],
};

function isCountry(string: unknown): string is Country {
  return typeof string === 'string' && country.includes(string as Country);
}

/**
 * @deprecated This function is scheduled for deprecation. The calculation of `paymentMethodShown` will be handled by `@seek/payments-widget`.
 */
// used by Caja page to get supported payment methods
export const getSupportedPaymentMethodsForCaja = (
  inputCountry: Country | string,
): PaymentMethodType[] => {
  if (isCountry(inputCountry)) {
    return paymentMethodsByCountry[inputCountry].filter((paymentMethod) => {
      if (inputCountry === 'ID') {
        return paymentMethod !== 'IdVirtualAccount';
      }
      return true;
    });
  }

  // when unsupported countries, should default to AU paymentMethods
  return paymentMethodsByCountry.AU;
};

/**
 * @deprecated This function is scheduled for deprecation. The calculation of `paymentMethodShown` will be handled by `@seek/payments-widget`.
 */
// used by 'Invoice' page to get supported payment methods
export const getSupportedPaymentMethodsForInvoices = (
  inputCountry: Country | string,
  isMultipleInvoice: boolean,
): PaymentMethodType[] => {
  const paymentMethods: PaymentMethodType[] = [];

  if (isCountry(inputCountry)) {
    const methodsForCountry = paymentMethodsByCountry[inputCountry];
    for (const paymentMethod of methodsForCountry) {
      // filter invoice for PaMi
      if (paymentMethod !== 'Invoice') paymentMethods.push(paymentMethod);
    }
  }

  if (isMultipleInvoice) {
    // filter out 2c2p payment methods and stripe id virtual account
    return paymentMethods.filter(
      (paymentMethod) =>
        paymentMethod !== 'IdVirtualAccount' &&
        !paymentMethodsIn2c2p.includes(paymentMethod),
    );
  }
  return paymentMethods;
};

type IDOnlineBankingBankAbbr = 'bca' | 'bni' | 'bri' | 'cimb' | 'mandiri';

const getSupportedIDBank2C2P: Array<{
  value: IDOnlineBankingBankAbbr;
  bankname: string;
}> = [
  {
    value: 'bca',
    bankname: 'BCA (Bank Central Asia)',
  },
  {
    value: 'bni',
    bankname: 'BNI (Bank Negara Indonesia)',
  },
  {
    value: 'bri',
    bankname: 'BRI',
  },
  {
    value: 'cimb',
    bankname: 'CIMB',
  },
  {
    value: 'mandiri',
    bankname: 'Mandiri',
  },
];

type PHOnlineBankingBankAbbr = 'bpi' | 'unionbank';

const getSupportedPHBank2C2P: Array<{
  value: PHOnlineBankingBankAbbr;
  bankname: string;
}> = [
  {
    value: 'bpi',
    bankname: 'BPI (Bank of the Philippine Islands)',
  },
  // {
  //   value: 'unionbank',
  //   bankname: 'UnionBank (UnionBank of the Philippines)',
  // },
];

type THOnlineBankingBankAbbr = 'bbl' | 'bay' | 'kbank' | 'scb' | 'tmb' | 'uob';

const getSupportedTHBank2C2P: Array<{
  value: THOnlineBankingBankAbbr;
  bankname: string;
}> = [
  {
    value: 'bbl',
    bankname: 'Bangkok Bank PCL',
  },
  {
    value: 'bay',
    bankname: 'Bank of Ayudhya',
  },
  {
    value: 'kbank',
    bankname: 'Kasikorn Bank',
  },
  {
    value: 'scb',
    bankname: 'Siam Commercial Bank',
  },
  {
    value: 'tmb',
    bankname: 'TMBThanachart Bank Public Company Limited',
  },
  {
    value: 'uob',
    bankname: 'United Overseas Bank',
  },
];

export const getSupportedStripeBankTransfer: Array<{
  bankcode: string;
  value: VirtualAccountBankAbbr;
  bankname: string;
}> = [
  {
    bankcode: '014',
    value: 'bca',
    bankname: 'BCA (Bank Central Asia)',
  },
  {
    bankcode: '009',
    value: 'bni',
    bankname: 'BNI (Bank Negara Indonesia)',
  },
  {
    bankcode: '008',
    value: 'mandiri',
    bankname: 'Mandiri',
  },
  {
    bankcode: '009',
    value: 'other',
    bankname: 'Others',
  },
];

export type CountryHaveBank2c2p = Exclude<
  Country,
  'AU' | 'HK' | 'MY' | 'NZ' | 'SG' | 'VN'
>;

export const getSupported2C2PBank: Record<
  CountryHaveBank2c2p,
  Array<{
    value:
      | IDOnlineBankingBankAbbr
      | PHOnlineBankingBankAbbr
      | THOnlineBankingBankAbbr;
    bankname: string;
  }>
> = {
  ID: getSupportedIDBank2C2P,
  PH: getSupportedPHBank2C2P,
  TH: getSupportedTHBank2C2P,
};

const getSupportedIDEwallet2C2P: Array<{
  value: EwalletType;
  ewalletname: string;
}> = [
  {
    value: 'DANA',
    ewalletname: 'DANA',
  },
  {
    value: 'LINKAJA',
    ewalletname: 'LinkAja',
  },
  {
    value: 'OVO',
    ewalletname: 'OVO',
  },
];

const getSupportedPHEwallet2C2P: Array<{
  value: EwalletType;
  ewalletname: string;
}> = [
  {
    value: 'GCASH',
    ewalletname: 'GCash',
  },
  {
    value: 'GRAB',
    ewalletname: 'GrabPay',
  },
  {
    value: 'PAYMAYA',
    ewalletname: 'Maya',
  },
];

const getSupportedHKEwallet2C2P: Array<{
  value: EwalletType;
  ewalletname: string;
}> = [
  {
    value: 'ALIPAYHK',
    ewalletname: 'AliPay (HK)',
  },
  // {
  //   value: 'WECHAT',
  //   ewalletname: 'WeChat Pay (CN)',
  // },
];

export type CountryHaveEWallet2c2p = Exclude<
  Country,
  'AU' | 'MY' | 'NZ' | 'SG' | 'TH' | 'VN'
>;

export const getSupportedEwallet2C2P: Record<
  CountryHaveEWallet2c2p,
  Array<{ value: EwalletType; ewalletname: string }>
> = {
  ID: getSupportedIDEwallet2C2P,
  PH: getSupportedPHEwallet2C2P,
  HK: getSupportedHKEwallet2C2P,
};
